import React, { Component, useEffect, useRef } from 'react'
import { inject, observer } from 'mobx-react'
import { extendObservable } from 'mobx'
import ContractSelector from '../../components/contractSelector'
import { Link } from 'react-router-dom'
import MobxReactForm from 'mobx-react-form'
import validator from 'validatorjs'
import fields from './repairsAndInquiriesFields'
import { Input, CustomSelect, Textarea, Checkbox, ImageUploader } from '../../components/formComponents'
import t from '../../service/translate.service'
import LoadingPageOverlay from '../../components/LoadingPageOverlay'
import ThankYouOverlay from '../../components/ThankYouOverlay'
import { Form } from 'react-bootstrap'
import { PropTypes } from 'prop-types'
import resizer from 'react-image-file-resizer'
import historyService from '../../service/history.service'
import { Alert } from 'antd'
import { buildValidators } from '../../service/validate'
import * as dataPreserveService from '../../service/dataPreserve.service'
import { appFeatureEnabled } from '../../service/appFeatures.service'

const plugins = { dvr: validator }

class RepairsAndInquiriesForm extends Component {
  constructor(props) {
    super(props)

    extendObservable(this, {
      store: this.props.store,
      files: [],
      urgent: false
    })

    this.form = new MobxReactForm({ fields }, { plugins })
  }

  componentDidMount = () => {
    window.scrollTo(0, 0)
  }

  onSubmit = (e, generateErrorList) => {
    e.preventDefault()
    this.store.submit(this.props.contractStore.selectedContract.contractNumber, generateErrorList)
  }

  removeImg = (e, img) => {
    e.preventDefault()
  }

  resizeFile = (file) =>
    new Promise((resolve) => {
      resizer.imageFileResizer(file, 1600, 1600, 'jpeg', 90, 0,
        (uri) => {
          resolve(uri)
        },
        'file'
      )
    })

  onImageDrop = async (files) => {
    const resizedImg = files.map(async (file) => {

      if(file.type.includes('image/')) {
        const image = await this.resizeFile(file)
        image.preview = file.preview
        return image        
      }

      return file
    })
    const resized = await Promise.all(resizedImg)
    this.store.addFile(resized)
  }

  onError() {
    return true
  }

  render() {
    if (this.store.submitting) {
      return <LoadingPageOverlay />
    }
    if (this.store.submitSuccess) {
      return <ThankYouOverlay
        title={t.THANK_YOU}
        description={t.DAMAGE_REPORT_SUCCESS}
        buttonText={t.BACK_TO_REPAIRS_AND_INQUIRES}
        buttonLink='/repairs-and-inquiries'
      />
    }
    return (
      <RenderForm
        store={this.store}
        onSubmit={this.onSubmit}
        onImageDrop={this.onImageDrop}
      />
    )
  }
}

const RenderForm = observer(({ onSubmit, onImageDrop, store }) => {
  const urlParams = new URLSearchParams(window.location.search);
  const queryParamValue = urlParams.get('contractNumber');
  const { model, setVal, files, validators, categories, error, validatorInputs, submitted, errorListVoice, errorsList } = store
  const inputRefs = useRef({})
  const validatorBlur = buildValidators(validatorInputs, true)
  // DEPRECATED BY DOKUWEB - Feature flag check no longer needed once dokuweb is fully tested
  const dokuwebEnabled = appFeatureEnabled("dokuwebEnabled")

  useEffect(() => {
    const data = dataPreserveService.getData('repairs_and_inquiries')

    if (data) {
      if (data.subject) {
        setVal('subject', data.subject)
      }
      if (data.category) {
        setVal('repairAndInquiryCategoryId', data.category)
      }
      if (data.description) {
        setVal('description', data.description)
      }
      if (data.urgent) {
        setVal('urgent', data.urgent)
      }

      dataPreserveService.removeData('repairs_and_inquiries')
    }
  }, [])

  const generateErrorList = () => {
    const errors = []
    for (let key in inputRefs?.current) {
      const item = inputRefs?.current[key]
      if (validatorBlur[key] && validatorBlur[key](item.value, item.ariaLabel)) {
        let err = validatorBlur[key](item.value, item.ariaLabel)
        errors.push(
          <button
            key={key}
            type='button'
            onClick={() => {
              item.focus()
            }}
          >
            {err}
          </button>)        
      }
    }
    return errors
  }

  const AlertContent = () => {
    if (errorsList?.length > 0) {
      window.scrollTo(0, 0)
      return (
        <Alert
          role={errorListVoice ? "alert" : "none"}
          message={
            <div
              className='error-list'
              role={errorListVoice ? "alert" : "none"}
              aria-live={errorListVoice ? "assertive" : "off"}
            >
              {t.REQUIRED_FIELD_MISSING}
              {errorsList?.map((err) => {
                return err
              })}
            </div>
          }
          type='error'
          style={{ marginTop: '12px', marginBottom: '12px' }}
        />
      )
    } else {
      return (<></>)
    }
  }

  return (
    <Form>
      {error && <Alert aria-live="assertive" role='alert' message={error} type='error' style={{marginTop: '12px', marginBottom: '12px'}} />}
      {submitted && (
        <AlertContent />
      )}
      <ContractSelector required={true} contractNumber={queryParamValue || undefined} label padding aria-label={t['DAMAGE_REPORT_CONTRACT_LABEL']} />
      <Input
        ref={(el) => (inputRefs.current['subject'] = el)}
        name="subject"
        value={model.subject}
        validate={validators['subject']}
        blurValidate={validatorBlur['subject']}
        label={t['DAMAGE_REPORT_SUBJECT']}
        required={true}
        onChange={e => setVal('subject', e.target.value)}
        aria-describedby="subject-helper"
      />
  
      {/* DEPRECATED BY DOKUWEB - Category field hidden when dokuweb enabled, can remove condition once dokuweb is fully tested */}
      {!dokuwebEnabled && <CustomSelect
        value={model.repairAndInquiryCategoryId}
        label={t['DAMAGE_REPORT_CATEGORY']}
        options={categories}
        onChange={val => setVal('repairAndInquiryCategoryId', val)}
        aria-label={t['DAMAGE_REPORT_CATEGORY_LABEL']}
      />}
  
      <Textarea
        ref={(el) => (inputRefs.current['description'] = el)}
        name="description"
        value={model.description}
        validate={validators['description']}
        blurValidate={validatorBlur['description']}
        label={t['DAMAGE_REPORT_DESCRIPTION']}
        required={true}
        placeholder={t['DAMAGE_REPORT_DESCRIPTION_PLACEHOLDER']}
        onChange={e => setVal('description', e.target.value)}
        aria-describedby="description-helper"
      />
  
      <ImageUploader
        label={t['DAMAGE_REPORT_UPLOAD_IMAGE']}
        files={files}
        onImageDrop={onImageDrop}
        aria-label={t['DAMAGE_REPORT_UPLOAD_IMAGE_LABEL']}
      />
  
      <Checkbox
        label={t['DAMAGE_REPORT_URGENT']}
        value={model.urgent}
        onChange={e => setVal('urgent', !model.urgent)}
        validate={validators['urgent']}
        aria-label={t['DAMAGE_REPORT_URGENT_LABEL']}
      />

      <Form.Group className='actions text-center'>
        <div className='group'>
          <button
            className='button primary'
            onClick={(e) => {
              if (e.key !== 'Tab') {
                e.preventDefault()
              }
              try {
                onSubmit(e, generateErrorList)
              } catch (error) {
                dataPreserveService.saveData('repairs_and_inquiries', {
                  subject: model.subject, 
                  category: model.repairAndInquiryCategoryId,
                  description: model.description,
                  urgent: model.urgent,
                })
              }
            }}
            aria-label={t.FORM_SUBMIT}
          >
            {t.FORM_SUBMIT}
          </button>
          <Link to='/repairs-and-inquiries' className='button secondary' onClick={() => historyService.push('/repairs-and-inquiries')} aria-label={t.FORM_CANCEL}>{t.FORM_CANCEL}</Link>
        </div>
      </Form.Group>
    </Form>
  );
})

RepairsAndInquiriesForm.propTypes = {
  contractStore: PropTypes.object,
  repairsAndInquiriesFormStore: PropTypes.object
}

export default inject('contractStore')(observer(RepairsAndInquiriesForm))
